import { jsxs, jsx } from "react/jsx-runtime";
import { forwardRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSensors, useSensor, DndContext, closestCenter, DragOverlay, KeyboardSensor, TouchSensor, MouseSensor } from "../../../node_modules/.pnpm/@dnd-kit_core@6.3.1_react-dom@18.3.1_react@18.3.1__react@18.3.1/node_modules/@dnd-kit/core/dist/core.esm.js";
import { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "../../../node_modules/.pnpm/@dnd-kit_sortable@8.0.0_@dnd-kit_core@6.3.1_react-dom@18.3.1_react@18.3.1__react@18.3.1__react@18.3.1/node_modules/@dnd-kit/sortable/dist/sortable.esm.js";
import { CSS } from "../../../node_modules/.pnpm/@dnd-kit_utilities@3.2.2_react@18.3.1/node_modules/@dnd-kit/utilities/dist/utilities.esm.js";
import { cn } from "../../../lib/utils.js";
import { formatText } from "../../../utils/quiz-parser.js";
import { useOrderingQuiz } from "../hooks/use-ordering-quiz.js";
import "../shared/quiz-blank.js";
import { KatexText } from "../shared/katex-text.js";
import "../shared/quiz-style-context.js";
import { QuizResultToggle } from "../shared/quiz-result-toggle.js";
import { SubmitButton } from "../shared/submit-button.js";
import "../../../node_modules/.pnpm/hls.js@1.6.15/node_modules/hls.js/dist/hls.js";
import "../../../context/DevToolsContext.js";
import { QuizHeader } from "../shared/quiz-header.js";
import { QuizSolveDescription } from "../shared/quiz-solve-description.js";
import { reorderOrderingChoices, ORDERING_DROP_ANIMATION, ORDERING_TOUCH_ACTIVATION_CONSTRAINT, ORDERING_MOUSE_ACTIVATION_CONSTRAINT } from "../ordering-dnd.js";
import ArrowRight from "../../../node_modules/.pnpm/lucide-react@0.309.0_react@18.3.1/node_modules/lucide-react/dist/esm/icons/arrow-right.js";
const restrictToHorizontal = ({ transform }) => ({
  ...transform,
  y: 0
});
const restrictToVertical = ({ transform }) => ({
  ...transform,
  x: 0
});
const OrderingStudentCard = forwardRef(
  function OrderingStudentCard2({
    answer,
    index,
    isCorrect,
    isSolved,
    isDragging,
    isOverlay = false,
    className,
    ...props
  }, ref) {
    return /* @__PURE__ */ jsxs(
      "div",
      {
        ref,
        className: cn(
          "group relative flex items-center justify-center w-full max-w-[400px] h-[80px] mx-auto md:h-[176px] px-4 py-3 border-2 rounded-3xl bg-white transition-all",
          "touch-manipulation select-none",
          "[box-shadow:0px_4px_0px_0px_rgba(0,0,0,0.10)]",
          isDragging && "border-lc-lightblue-900 bg-lc-lightblue-100 text-lc-lightblue-900 [box-shadow:0px_8px_16px_0px_rgba(0,0,0,0.20)] z-10",
          isDragging && !isOverlay && "opacity-30",
          !isDragging && !isSolved && "border-lc-gray-400 text-lc-gray-800 hover:border-lc-lightblue-900 hover:text-lc-lightblue-900 hover:[box-shadow:0px_4px_0px_0px_rgba(85,195,255,0.30)]",
          isSolved && isCorrect === true && "border-lc-mint-900 bg-lc-mint-100 text-lc-mint-dark [box-shadow:unset]",
          isSolved && isCorrect === false && "border-lc-red-900 bg-lc-red-100 text-lc-red-900 [box-shadow:unset]",
          isSolved && "pointer-events-none",
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx("div", { className: cn(
            "absolute left-1/2 -translate-x-1/2 -top-4 flex items-center justify-center rounded-full font-bold transition-all",
            isDragging && "w-20 h-8 bg-lc-lightblue-900 text-white border-none",
            !isDragging && !isSolved && "w-8 h-8 border-2 border-lc-gray-400 bg-[#f5f5f5] text-[#999] group-hover:bg-lc-lightblue-200 group-hover:border-lc-lightblue-900 group-hover:text-lc-lightblue-900",
            isSolved && isCorrect === true && "w-8 h-8 bg-lc-mint-900 text-white border-none",
            isSolved && isCorrect === false && "w-8 h-8 bg-lc-red-900 text-white border-none"
          ), children: index + 1 }),
          answer.image && /* @__PURE__ */ jsx(
            "img",
            {
              src: answer.image.signedUrl || answer.image.url,
              alt: `item-${index}`,
              className: "max-h-24 w-auto object-contain"
            }
          ),
          answer.answer && /* @__PURE__ */ jsx("span", { className: "text-[20px] font-medium text-center", children: /* @__PURE__ */ jsx(KatexText, { text: answer.answer }) })
        ]
      }
    );
  }
);
function SortableItem({ id, answer, index, isCorrect, isSolved }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 99 : "auto"
  };
  return /* @__PURE__ */ jsx(
    OrderingStudentCard,
    {
      ref: setNodeRef,
      style,
      answer,
      index,
      isCorrect,
      isSolved,
      isDragging,
      ...attributes,
      ...listeners
    }
  );
}
function OrderingStudent({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  submitLabel,
  hideSubmitButton = false,
  onAnswerChange,
  disableSound: _disableSound = false,
  difficultyLevel,
  hideExplanation = false,
  hideSolveDescription = false,
  dimChoiceText = false
}) {
  const { t } = useTranslation("quiz");
  const [activeId, setActiveId] = useState(null);
  const {
    orderedChoices,
    setOrderedChoices,
    isVertical,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleSubmit
  } = useOrderingQuiz({
    quizId,
    quiz,
    onSolve,
    solveResult,
    showReallyTrue,
    isViewSolved,
    initialAnswers: initialAnswersProp,
    onAnswerChange
  });
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: ORDERING_MOUSE_ACTIVATION_CONSTRAINT
    }),
    useSensor(TouchSensor, {
      activationConstraint: ORDERING_TOUCH_ACTIVATION_CONSTRAINT
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );
  const handleDragStart = useCallback((event) => {
    setActiveId(String(event.active.id));
  }, []);
  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    setOrderedChoices((prev) => {
      return reorderOrderingChoices(prev, String(active.id), String(over.id));
    });
  }, [setOrderedChoices]);
  const displayChoices = orderedChoices;
  const activeIndex = activeId === null ? -1 : displayChoices.findIndex((choice) => choice.key === activeId);
  const activeChoice = activeIndex >= 0 ? displayChoices[activeIndex] : null;
  const getArrowColor = () => {
    if (!isSolved) return "text-lc-lightblue-900";
    if ((solveResult == null ? void 0 : solveResult.correct) || shouldShowCorrectAnswers) return "text-lc-mint-900";
    return "text-lc-red-900";
  };
  return /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(
      QuizHeader,
      {
        quizId,
        typeLabel: t("types.ordering.label"),
        title: quiz.title,
        difficultyLevel,
        variant: "student",
        image: quiz.image,
        imageMobile: quiz.imageMobile,
        desmos: quiz.desmos,
        vars: quiz.vars,
        dimTitleText: dimChoiceText,
        dimMedia: dimChoiceText,
        problem: quiz.problem && /* @__PURE__ */ jsx("div", { className: "text-xl leading-relaxed text-gray-900", children: formatText(quiz.problem, `${quizId}-problem`, quiz.additionalImages ?? void 0) })
      }
    ),
    !isSolved && !isViewSolved && /* @__PURE__ */ jsx("p", { className: "text-base text-gray-500", children: t("types.ordering.dragInstruction") }),
    /* @__PURE__ */ jsxs(
      DndContext,
      {
        sensors,
        collisionDetection: closestCenter,
        onDragStart: handleDragStart,
        onDragCancel: handleDragCancel,
        onDragEnd: handleDragEnd,
        modifiers: isVertical ? [restrictToVertical] : [restrictToHorizontal],
        children: [
          /* @__PURE__ */ jsx(
            SortableContext,
            {
              items: displayChoices.map((c) => c.key),
              strategy: isVertical ? verticalListSortingStrategy : horizontalListSortingStrategy,
              children: /* @__PURE__ */ jsx("div", { className: cn(
                isVertical ? "flex flex-col items-center justify-start space-y-14 p-2" : "flex flex-row items-center justify-between space-x-8 p-2"
              ), children: displayChoices.map((choice, index) => /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "relative",
                  style: !isVertical ? { width: `${100 / displayChoices.length}%` } : void 0,
                  children: [
                    index > 0 && /* @__PURE__ */ jsx(
                      ArrowRight,
                      {
                        strokeWidth: 3,
                        className: cn(
                          "absolute w-6 h-6",
                          getArrowColor(),
                          isVertical ? "left-1/2 -translate-x-1/2 -top-9 rotate-90" : "-left-5 top-1/2 -translate-y-1/2"
                        )
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      SortableItem,
                      {
                        id: choice.key,
                        answer: choice,
                        index,
                        isCorrect: isSolved ? shouldShowCorrectAnswers || ((solveResult == null ? void 0 : solveResult.correct) ?? false) : null,
                        isSolved
                      }
                    )
                  ]
                },
                choice.key
              )) })
            }
          ),
          /* @__PURE__ */ jsx(DragOverlay, { dropAnimation: ORDERING_DROP_ANIMATION, children: activeChoice ? /* @__PURE__ */ jsx(
            OrderingStudentCard,
            {
              answer: activeChoice,
              index: activeIndex,
              isSolved,
              isDragging: true,
              isOverlay: true
            }
          ) : null })
        ]
      }
    ),
    !isSolved && !isViewSolved && !hideSubmitButton && /* @__PURE__ */ jsx(
      SubmitButton,
      {
        onClick: handleSubmit,
        disabled: !canSubmit,
        label: submitLabel ?? t("common.checkAnswer")
      }
    ),
    isSolved && !hideExplanation && /* @__PURE__ */ jsx(
      QuizResultToggle,
      {
        value: isShowingCorrectAnswers ? "correct-and-explanation" : "my-answer",
        onChange: (next) => setIsShowingCorrectAnswers(next === "correct-and-explanation")
      }
    ),
    isSolved && isShowingCorrectAnswers && !hideExplanation && !hideSolveDescription && /* @__PURE__ */ jsx(
      QuizSolveDescription,
      {
        description: (solveResult == null ? void 0 : solveResult.description) ?? void 0,
        commentaryImages: quiz.commentaryImages,
        vars: quiz.vars,
        id: `${quizId}-desc`,
        actionVisible: false
      }
    )
  ] });
}
export {
  OrderingStudent
};
//# sourceMappingURL=ordering-student.js.map
